import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Trash2, Calendar, ChevronDown, ChevronRight, Share2, Loader2, LayoutGrid, List, MessageSquare, Pin } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';
import { SearchableSelect } from '@/components/SearchableSelect';
import TaskDetail from '@/components/TaskDetail';
import ShareDialog from '@/components/ShareDialog';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';

const STATUS_LIST = ['new', 'in_progress', 'on_hold', 'completed', 'cancelled', 'overdue'];
const STATUS_LABELS = { new: 'New', in_progress: 'In Progress', on_hold: 'On Hold', completed: 'Completed', cancelled: 'Cancelled', overdue: 'Overdue' };
const STATUS_COLORS = { new: 'bg-amber-500', in_progress: 'bg-blue-500', on_hold: 'bg-orange-500', completed: 'bg-emerald-500', cancelled: 'bg-red-500', overdue: 'bg-rose-600' };
const PRIORITIES = { high: 'priority-high', medium: 'priority-medium', low: 'priority-low' };
const PRIORITY_LIST = ['high', 'medium', 'low'];
const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };
const PRIORITY_COLORS = { high: 'bg-rose-500', medium: 'bg-amber-500', low: 'bg-slate-400' };
const COLOR_OPTIONS = [
  { name: 'Red', value: '#ef4444' }, { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' }, { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' }, { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' }, { name: 'Teal', value: '#14b8a6' },
];

export default function TasksPage() {
  const { activeProject, projects } = useApp();
  useEscapeToHome();

  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('board');
  const [groupBy, setGroupBy] = useState('status'); // 'status' or 'priority'
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [selectedTask, setSelectedTask] = useState(null); // full-screen detail
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarksText, setRemarksText] = useState('');
  const [pendingDrop, setPendingDrop] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set([...STATUS_LIST, ...PRIORITY_LIST]));
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [shareTask, setShareTask] = useState(null);

  const [form, setForm] = useState({
    title: '', project_id: '', priority: 'medium', description: '', tags: '', color_tags: [],
    start_date: '', due_date: '', is_recurring: false, recurrence_pattern: '', subtasks: []
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tasks', { params: { project_id: activeProject, page: 1, limit: 200 } });
      setTasks(data.tasks);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [activeProject]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // 'N' to create (global), 'V' to toggle view mode, 'G' to toggle grouping (only when not typing)
  useEffect(() => {
    const h = (e) => {
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'n' && !isTyping) {
        e.preventDefault();
        setForm({ title: '', project_id: activeProject === 'all' ? '' : activeProject, priority: 'medium', description: '', tags: '', color_tags: [], start_date: '', due_date: '', is_recurring: false, recurrence_pattern: '', subtasks: [] });
        setShowForm(true);
      } else if (e.key === 'v' && !isTyping) {
        e.preventDefault();
        setViewMode(m => m === 'board' ? 'list' : 'board');
      } else if (e.key === 'g' && !isTyping) {
        e.preventDefault();
        setGroupBy(g => g === 'status' ? 'priority' : 'status');
      }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  });

  // Refresh selected task when tasks change
  useEffect(() => {
    if (selectedTask) {
      const updated = tasks.find(t => t.id === selectedTask.id);
      if (updated) setSelectedTask(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  // Group tasks
  const grouped = {};
  const groupList = groupBy === 'priority' ? PRIORITY_LIST : STATUS_LIST;
  const groupLabels = groupBy === 'priority' ? PRIORITY_LABELS : STATUS_LABELS;
  const groupColors = groupBy === 'priority' ? PRIORITY_COLORS : STATUS_COLORS;
  groupList.forEach(s => { grouped[s] = []; });
  tasks.forEach(t => {
    const key = groupBy === 'priority' ? (t.priority || 'medium') : (t.status || 'new');
    if (grouped[key]) grouped[key].push(t); else grouped[groupList[0]].push(t);
  });

  // ─── Form handlers ───
  const openCreate = () => {
    setForm({ title: '', project_id: activeProject === 'all' ? '' : activeProject, priority: 'medium', description: '', tags: '', color_tags: [], start_date: '', due_date: '', is_recurring: false, recurrence_pattern: '', subtasks: [] });
    setShowForm(true);
  };

  const handleFormKeyDown = (e, idx, total) => {
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (idx >= total - 1) save(); else focusFormField(idx + 1); }
  };
  const focusFormField = (idx) => { setTimeout(() => { const el = document.querySelector(`[data-form-idx="${idx}"]`); if (el) { el.focus(); if (el.getAttribute('role') === 'combobox') el.click(); } }, 60); };

  const save = async () => {
    if (!form.title || !form.project_id) { toast.error('Title and workspace required'); return; }
    try {
      await api.post('/tasks', { ...form, status: 'new', tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], start_date: form.start_date || null, due_date: form.due_date || null, recurrence_pattern: form.recurrence_pattern || null });
      toast.success('Task created');
      setShowForm(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleColorTag = (c) => setForm(p => ({ ...p, color_tags: p.color_tags.includes(c) ? p.color_tags.filter(x => x !== c) : [...p.color_tags, c] }));
  const addSubtask = () => setForm(p => ({ ...p, subtasks: [...p.subtasks, { title: '', is_completed: false }] }));
  const removeSubtask = (i) => setForm(p => ({ ...p, subtasks: p.subtasks.filter((_, j) => j !== i) }));

  const focusLastSubtask = () => {
    setTimeout(() => {
      const inputs = document.querySelectorAll('[data-subtask-input]');
      const last = inputs[inputs.length - 1];
      if (last) last.focus();
    }, 60);
  };

  // Enter in description: add a subtask row and focus it
  const onDescriptionEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addSubtask();
      focusLastSubtask();
    }
  };

  // Enter in a subtask input:
  //  - if current has text → add new blank, focus it
  //  - if current is empty → drop this blank and save
  const onSubtaskEnter = (e, idx) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    const current = (form.subtasks[idx]?.title || '').trim();
    if (current) {
      addSubtask();
      focusLastSubtask();
    } else {
      // Remove this empty row then save
      const trimmed = form.subtasks.filter((_, j) => j !== idx);
      const payload = { ...form, subtasks: trimmed };
      setForm(payload);
      setTimeout(() => saveWithPayload(payload), 20);
    }
  };

  const saveWithPayload = async (payload) => {
    if (!payload.title || !payload.project_id) { toast.error('Title and workspace required'); return; }
    try {
      await api.post('/tasks', { ...payload, status: 'new', tags: payload.tags ? payload.tags.split(',').map(t => t.trim()).filter(Boolean) : [], start_date: payload.start_date || null, due_date: payload.due_date || null, recurrence_pattern: payload.recurrence_pattern || null });
      toast.success('Task created');
      setShowForm(false);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Inside task form dialog: 'n' adds a new subtask row (one at a time)
  useEffect(() => {
    if (!showForm) return;
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'n' && !isTyping) {
        e.preventDefault();
        e.stopPropagation();
        addSubtask();
        // Focus the newly added subtask input
        setTimeout(() => {
          const inputs = document.querySelectorAll('[data-subtask-input]');
          const last = inputs[inputs.length - 1];
          if (last) last.focus();
        }, 80);
      }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [showForm]);

  // ─── Drag & Drop ───
  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const taskId = active.id;
    const newStatus = over.id; // column id = status
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.status === newStatus) return;

    if (newStatus === 'on_hold' || newStatus === 'cancelled') {
      setPendingDrop({ taskId, newStatus });
      setRemarksText('');
      setShowRemarks(true);
    } else {
      try {
        await api.put(`/tasks/${taskId}`, { status: newStatus });
        toast.success(`Moved to ${STATUS_LABELS[newStatus]}`);
        fetchTasks();
      } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    }
  };

  const confirmDropRemarks = async () => {
    if (!remarksText.trim()) { toast.error('Remarks required'); return; }
    try {
      await api.put(`/tasks/${pendingDrop.taskId}`, { status: pendingDrop.newStatus, remarks: remarksText });
      toast.success(`Moved to ${STATUS_LABELS[pendingDrop.newStatus]}`);
      setShowRemarks(false);
      setPendingDrop(null);
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // ─── Bulk ───
  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const selectAllTasks = () => {
    if (tasks.length > 0 && tasks.every(t => selected.has(t.id))) setSelected(new Set());
    else setSelected(new Set(tasks.map(t => t.id)));
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} task(s)?`)) return;
    try { await api.post('/bulk-delete', { record_type: 'task', record_ids: [...selected] }); toast.success(`${selected.size} moved to trash`); setSelected(new Set()); fetchTasks(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteTask = async (id) => {
    if (!window.confirm('Move this task to trash?')) return;
    try { await api.delete(`/tasks/${id}`); toast.success('Task moved to trash'); fetchTasks(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleGroup = (s) => setExpandedGroups(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const toggleExpandTask = (id) => setExpandedTasks(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const draggedTask = activeId ? tasks.find(t => t.id === activeId) : null;

  // Full-screen task detail
  if (selectedTask) {
    return <TaskDetail task={selectedTask} projects={projects} onClose={() => setSelectedTask(null)} onRefresh={fetchTasks} />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="tasks-page">
      {/* ─── Fixed Header ─── */}
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Tasks</h2>
            <p className="text-sm text-zinc-500 mt-1">{total} task{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Select All */}
            {viewMode === 'list' && tasks.length > 0 && (
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer mr-2" data-testid="select-all-tasks">
                <Checkbox checked={tasks.length > 0 && tasks.every(t => selected.has(t.id))} onCheckedChange={selectAllTasks} className="h-3.5 w-3.5" />
                <span>All</span>
              </label>
            )}
            {/* Group by toggle */}
            <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-400 mr-2">
              <span>Group:</span>
              <button onClick={() => setGroupBy('status')} className={`px-2 py-1 rounded ${groupBy === 'status' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="group-by-status">Status</button>
              <button onClick={() => setGroupBy('priority')} className={`px-2 py-1 rounded ${groupBy === 'priority' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="group-by-priority">Priority</button>
            </div>
            {/* View toggle */}
            <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
              <button onClick={() => setViewMode('board')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'board' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="view-board">
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Board</button>
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="view-list">
                <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
            </div>
            <Button onClick={openCreate} className="rounded-xl" data-testid="create-task-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New Task</Button>
          </div>
        </div>
      </div>

      {/* ─── Scrollable Content ─── */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-zinc-500 text-sm mb-4">No tasks found.</p>
            <Button onClick={openCreate} variant="outline"><Plus className="h-4 w-4 mr-1" /> New Task</Button>
          </div>
        ) : viewMode === 'board' ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex gap-3 h-full overflow-x-auto px-4 md:px-6 py-4" data-testid="board-view">
              {groupList.map(grp => (
                <BoardColumn key={grp} groupKey={grp} label={groupLabels[grp]} color={groupColors[grp]}
                  tasks={grouped[grp]} projectMap={projectMap}
                  onClickTask={setSelectedTask} onAddTask={openCreate} isDndEnabled={groupBy === 'status'}
                  onShareTask={setShareTask} onDeleteTask={deleteTask} />
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {draggedTask && (
                <div className="bg-white rounded-xl border-2 border-blue-400 p-3 shadow-2xl w-72 rotate-[2deg]">
                  <h4 className="text-sm font-medium mb-1">{draggedTask.title}</h4>
                  <p className="text-[11px] text-zinc-400">{projectMap[draggedTask.project_id]}</p>
                  <Badge className={`text-[10px] px-1.5 py-0 mt-1 ${PRIORITIES[draggedTask.priority]}`}>{draggedTask.priority}</Badge>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="overflow-y-auto h-full p-4 md:px-8">
            <div className="w-full space-y-4">
              {groupList.map(grp => {
                const items = grouped[grp];
                const isOpen = expandedGroups.has(grp);
                return (
                  <div key={grp} data-testid={`list-group-${grp}`}>
                    <button onClick={() => toggleGroup(grp)} className="flex items-center gap-2 mb-2 w-full text-left py-1.5 px-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors" data-testid={`toggle-group-${grp}`}>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                      <Badge className={`${groupColors[grp]} text-white text-[10px] px-2 py-0.5`}>{groupLabels[grp]}</Badge>
                      <span className="text-xs text-zinc-400">{items.length}</span>
                    </button>
                    {isOpen && items.length > 0 && (
                      <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden">
                        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-zinc-50 dark:bg-zinc-900/50 text-[11px] font-medium text-zinc-400 uppercase tracking-wider border-b border-zinc-200 dark:border-zinc-800">
                          <div className="col-span-1" onClick={e => e.stopPropagation()}><Checkbox checked={items.length > 0 && items.every(t => selected.has(t.id))} onCheckedChange={() => { const allSelected = items.every(t => selected.has(t.id)); setSelected(prev => { const n = new Set(prev); items.forEach(t => allSelected ? n.delete(t.id) : n.add(t.id)); return n; }); }} className="h-3.5 w-3.5" data-testid={`select-all-${grp}`} /></div><div className="col-span-5">Name</div><div className="col-span-2 hidden md:block">Workspace</div><div className="col-span-2">Due date</div><div className="col-span-2">Priority</div>
                        </div>
                        {items.map(task => (
                          <div key={task.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors group items-center cursor-pointer"
                            onClick={() => setSelectedTask(task)} data-testid={`list-row-${task.id}`}>
                            <div className="col-span-1 flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Checkbox checked={selected.has(task.id)} onCheckedChange={() => toggleSelect(task.id)} className="h-3.5 w-3.5" />
                            </div>
                            <div className="col-span-5 flex items-center gap-2 min-w-0">
                              <span className={`text-sm font-medium truncate ${task.status === 'completed' ? 'line-through text-zinc-400' : ''}`}>{task.title}</span>
                              {(task.subtasks || []).length > 0 && <span className="text-[10px] text-zinc-400">{task.subtasks.length} sub</span>}
                              {(task.color_tags || []).map(c => <span key={c} className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c }} />)}
                            </div>
                            <div className="col-span-2 hidden md:block text-[11px] text-zinc-400 truncate">{projectMap[task.project_id] || ''}</div>
                            <div className="col-span-2 text-[11px] text-zinc-400">{task.due_date || '—'}</div>
                            <div className="col-span-2 flex items-center gap-1"><Badge className={`text-[10px] px-1.5 py-0 ${PRIORITIES[task.priority]}`}>{task.priority}</Badge>
                              {task.is_shared_with_me && <Badge variant="secondary" className="text-[9px] px-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">Shared</Badge>}
                              {!task.is_shared_with_me && <button onClick={(e) => { e.stopPropagation(); setShareTask(task); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded ml-auto shrink-0" data-testid={`list-share-${task.id}`}><Share2 className="h-3 w-3 text-zinc-500" /></button>}
                              {!task.is_shared_with_me && <button onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-950 rounded shrink-0" data-testid={`delete-task-${task.id}`}><Trash2 className="h-3 w-3 text-red-400" /></button>}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 bg-zinc-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50" data-testid="selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs underline opacity-70">Clear</button>
          <Button size="sm" variant="destructive" onClick={bulkDelete}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
      )}

      {/* ─── Create Task Form (no status field) ─── */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
          <DialogHeader><DialogTitle>New Task</DialogTitle><DialogDescription>Create a new task (status defaults to New)</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Task title" className="h-12" data-testid="task-title-input" data-form-idx="0" onKeyDown={e => handleFormKeyDown(e, 0, 7)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Workspace *</Label>
                <SearchableSelect value={form.project_id} onValueChange={v => setForm(p => ({...p, project_id: v}))}
                  options={projects.map(p => ({value: p.id, label: p.name}))} placeholder="Select workspace"
                  data-testid="task-project-select" data-form-idx="1" onMoveToNext={() => focusFormField(2)} /></div>
              <div className="space-y-2"><Label>Priority</Label>
                <SearchableSelect value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))}
                  options={[{value:'high',label:'High'},{value:'medium',label:'Medium'},{value:'low',label:'Low'}]}
                  placeholder="Priority" data-testid="task-priority-select" data-form-idx="2" onMoveToNext={() => focusFormField(3)} /></div>
            </div>
            <div className="space-y-2"><Label>Tags</Label>
              <Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="tag1, tag2" data-form-idx="3" tabIndex={0} onKeyDown={e => handleFormKeyDown(e, 3, 7)} /></div>
            <div className="space-y-2"><Label>Color Tags</Label>
              <div className="flex gap-2 flex-wrap">{COLOR_OPTIONS.map(c => (
                <button key={c.value} onClick={() => toggleColorTag(c.value)} className={`w-7 h-7 rounded-full border-2 transition-all ${form.color_tags.includes(c.value) ? 'border-zinc-900 dark:border-white scale-110' : 'border-transparent'}`} style={{backgroundColor: c.value}} />
              ))}</div></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} data-form-idx="4" tabIndex={0} onKeyDown={e => handleFormKeyDown(e, 4, 7)} /></div>
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} data-form-idx="5" tabIndex={0} onKeyDown={e => handleFormKeyDown(e, 5, 7)} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Description · Press Enter to add subtask" data-form-idx="6" tabIndex={0} onKeyDown={onDescriptionEnter} /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Subtasks</Label><Button variant="ghost" size="sm" onClick={addSubtask}><Plus className="h-3 w-3 mr-1" /> Add</Button></div>
              {form.subtasks.map((st, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={st.title} onChange={e => { const u = [...form.subtasks]; u[i] = {...u[i], title: e.target.value}; setForm(p => ({...p, subtasks: u})); }}
                    placeholder={`Subtask ${i+1} · Enter: add row · Empty Enter: save`} className="flex-1" data-subtask-input onKeyDown={e => onSubtaskEnter(e, i)} />
                  <Button variant="ghost" size="icon" onClick={() => removeSubtask(i)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                </div>))}
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-xl" data-testid="task-save-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remarks dialog for drag-to-hold/cancel */}
      <Dialog open={showRemarks} onOpenChange={setShowRemarks}>
        <DialogContent data-testid="remarks-dialog">
          <DialogHeader>
            <DialogTitle>Remarks Required</DialogTitle>
            <DialogDescription>Please provide a reason for {STATUS_LABELS[pendingDrop?.newStatus]}</DialogDescription>
          </DialogHeader>
          <Textarea value={remarksText} onChange={e => setRemarksText(e.target.value)} placeholder="Enter remarks..." rows={3} data-testid="remarks-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRemarks(false); setPendingDrop(null); }}>Cancel</Button>
            <Button onClick={confirmDropRemarks} data-testid="confirm-remarks-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {shareTask && <ShareDialog open={!!shareTask} onClose={() => setShareTask(null)} recordType="task" recordId={shareTask.id} recordName={shareTask.title} />}
    </div>
  );
}

// ─── Board Column (droppable) ───
function BoardColumn({ groupKey, label, color, tasks, projectMap, onClickTask, onAddTask, isDndEnabled, onShareTask, onDeleteTask }) {
  const { setNodeRef, isOver } = useDroppable({ id: groupKey, disabled: !isDndEnabled });

  return (
    <div ref={setNodeRef} className={`flex-shrink-0 w-[calc((100vw-3rem)/2)] sm:w-[calc((100vw-4rem)/3)] md:w-0 md:flex-1 md:min-w-[200px] flex flex-col rounded-xl transition-colors ${isOver && isDndEnabled ? 'bg-blue-50 dark:bg-blue-950 ring-2 ring-blue-300 dark:ring-blue-600' : 'bg-zinc-50 dark:bg-zinc-900/50'}`}
      data-testid={`board-column-${groupKey}`}>
      <div className="flex items-center gap-2 p-3 shrink-0">
        <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
        <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
        <span className="text-xs text-zinc-400 ml-auto">{tasks.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2">
        {tasks.map(task => (
          <DraggableCard key={task.id} task={task} projectMap={projectMap} onClick={() => onClickTask(task)}
            onShare={() => onShareTask?.(task)} onDelete={() => onDeleteTask?.(task.id)} />
        ))}
      </div>
      <button onClick={() => onAddTask()} className="p-3 text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors flex items-center justify-center gap-1 shrink-0 border-t border-zinc-200 dark:border-zinc-800" data-testid={`add-task-${groupKey}`}>
        <Plus className="h-3 w-3" /> Add Task
      </button>
    </div>
  );
}

// ─── Draggable Card ───
function DraggableCard({ task, projectMap, onClick, onShare, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.3 : 1 } : undefined;
  const totalSubs = (task.subtasks || []).length;
  const completedSubs = (task.subtasks || []).filter(s => s.is_completed).length;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-3 hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing group relative"
      onClick={onClick} data-testid={`board-card-${task.id}`}>
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-zinc-900 rounded-md shadow-sm border border-zinc-200 dark:border-zinc-800 p-0.5">
        {!task.is_shared_with_me && <button onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onShare?.(); }} className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded" data-testid={`board-share-${task.id}`}><Share2 className="h-3 w-3 text-zinc-500" strokeWidth={1.5} /></button>}
        {!task.is_shared_with_me && <button onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onDelete?.(); }} className="p-1 hover:bg-red-50 dark:hover:bg-red-950 rounded" data-testid={`board-delete-${task.id}`}><Trash2 className="h-3 w-3 text-red-400" strokeWidth={1.5} /></button>}
      </div>
      <h4 className={`text-sm font-medium leading-snug mb-1 pr-14 ${task.status === 'completed' ? 'line-through text-zinc-400' : ''}`}>{task.title}</h4>
      <p className="text-[11px] text-zinc-400 mb-2">{projectMap[task.project_id] || 'Unknown'}</p>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge className={`text-[10px] px-1.5 py-0 ${PRIORITIES[task.priority]}`}>{task.priority}</Badge>
        {(task.color_tags || []).map(c => <span key={c} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />)}
        {task.is_pinned && <Pin className="h-3 w-3 text-zinc-900 fill-current" />}
        {task.is_shared_with_me && <Badge variant="secondary" className="text-[9px] px-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">Shared</Badge>}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-zinc-400 mt-2">
        {task.due_date && <span className="flex items-center gap-0.5"><Calendar className="h-3 w-3" />{task.due_date}</span>}
        {totalSubs > 0 && <span>{completedSubs}/{totalSubs} subtasks</span>}
        {(task.activity_log || []).length > 0 && <span className="flex items-center gap-0.5"><MessageSquare className="h-3 w-3" />{task.activity_log.length}</span>}
      </div>
      {task.remarks && <p className="text-[10px] text-amber-600 mt-1 truncate">Remarks: {task.remarks}</p>}
    </div>
  );
}
